import type {
  OpsCommandInsightsResponse,
  OpsCommandSeriesResponse,
  OpsConnectionsResponse,
  OpsDeviceDetailResponse,
  OpsDeviceHealthResponse,
  OpsDevicesResponse,
  OpsDlqResponse,
  OpsEventsFeedResponse,
  OpsLiveStatusResponse,
  OpsOverviewResponse,
  OpsQueueResponse,
  OpsRegistrySyncResponse,
  OpsRoutingMetaResponse,
  OpsSensorsSummaryResponse,
} from '@m8-app/contracts/internal/ops.js';
import type { CommandStatsResponse } from '@m8-app/contracts/internal/queries.js';
import { middlewareClient } from './client.js';

// The Device Plane dashboard — calls the middleware's /admin-ops/* endpoints
// directly (AdminAuthGuard: per-user JWT + Redis revocation check), hosted
// separately from the backend/admin panel. Same wire shapes as the
// service-token-guarded /internal/ops/*; only the guard/caller differs.

export async function getDevicePlaneOverview(): Promise<OpsOverviewResponse> {
  const { data } = await middlewareClient.get<OpsOverviewResponse>('/admin-ops/overview');
  return data;
}

export interface FleetFilters {
  presence?: string;
  family?: string;
  kind?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export async function getDevicePlaneDevices(filters: FleetFilters = {}): Promise<OpsDevicesResponse> {
  const { data } = await middlewareClient.get<OpsDevicesResponse>('/admin-ops/devices', {
    params: filters,
  });
  return data;
}

export async function getDevicePlaneDevice(deviceId: string): Promise<OpsDeviceDetailResponse> {
  const { data } = await middlewareClient.get<OpsDeviceDetailResponse>(
    `/admin-ops/devices/${deviceId}`,
  );
  return data;
}

export async function getDevicePlaneCommandSeries(params: {
  from?: string;
  to?: string;
  bucket?: 'hour' | 'day';
} = {}): Promise<OpsCommandSeriesResponse> {
  const { data } = await middlewareClient.get<OpsCommandSeriesResponse>(
    '/admin-ops/commands/series',
    { params },
  );
  return data;
}

export async function getDevicePlaneStats(params: {
  from?: string;
  to?: string;
  windowHours?: number;
} = {}): Promise<CommandStatsResponse> {
  const { data } = await middlewareClient.get<CommandStatsResponse>('/admin-ops/stats/commands', {
    params,
  });
  return data;
}

export async function getDevicePlaneQueue(): Promise<OpsQueueResponse> {
  const { data } = await middlewareClient.get<OpsQueueResponse>('/admin-ops/queue');
  return data;
}

export async function getDevicePlaneConnections(limit?: number): Promise<OpsConnectionsResponse> {
  const { data } = await middlewareClient.get<OpsConnectionsResponse>('/admin-ops/connections', {
    params: limit ? { limit } : {},
  });
  return data;
}

export async function getDevicePlaneLiveStatus(kind?: string): Promise<OpsLiveStatusResponse> {
  const { data } = await middlewareClient.get<OpsLiveStatusResponse>('/admin-ops/live-status', {
    params: kind ? { kind } : {},
  });
  return data;
}

export async function getDevicePlaneCommandInsights(
  windowHours?: number,
): Promise<OpsCommandInsightsResponse> {
  const { data } = await middlewareClient.get<OpsCommandInsightsResponse>(
    '/admin-ops/commands/insights',
    { params: windowHours ? { windowHours } : {} },
  );
  return data;
}

export async function getDevicePlaneEvents(limit?: number): Promise<OpsEventsFeedResponse> {
  const { data } = await middlewareClient.get<OpsEventsFeedResponse>('/admin-ops/events', {
    params: limit ? { limit } : {},
  });
  return data;
}

export async function getDevicePlaneRoutingMeta(): Promise<OpsRoutingMetaResponse> {
  const { data } = await middlewareClient.get<OpsRoutingMetaResponse>('/admin-ops/routing-meta');
  return data;
}

export async function getDevicePlaneDlq(limit?: number): Promise<OpsDlqResponse> {
  const { data } = await middlewareClient.get<OpsDlqResponse>('/admin-ops/pipeline/dlq', {
    params: limit ? { limit } : {},
  });
  return data;
}

export async function getDevicePlaneRegistrySync(): Promise<OpsRegistrySyncResponse> {
  const { data } = await middlewareClient.get<OpsRegistrySyncResponse>('/admin-ops/pipeline/registry-sync');
  return data;
}

export async function getDevicePlaneSensorsSummary(): Promise<OpsSensorsSummaryResponse> {
  const { data } = await middlewareClient.get<OpsSensorsSummaryResponse>('/admin-ops/sensors/summary');
  return data;
}

export async function getDevicePlaneDeviceHealth(): Promise<OpsDeviceHealthResponse> {
  const { data } = await middlewareClient.get<OpsDeviceHealthResponse>('/admin-ops/device-health');
  return data;
}
